import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import api from '../services/api';
import KpiCard from '../components/KpiCard';
import Avatar from '../components/Avatar';
import Modal from '../components/Modal';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const COLORS_BAR = ['#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6'];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [ext, setExt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { type, title }

  useEffect(() => {
    Promise.all([
      api.get('/admin/analytics').then(r => r.data),
      api.get('/admin/analytics/extended').then(r => r.data).catch(() => null),
    ]).then(([base, extended]) => {
      setData(base);
      setExt(extended);
    }).finally(() => setLoading(false));
  }, []);

  const exportCsv = () => {
    if (!data) return;
    const rows = [];
    rows.push(['== Funnel ==']);
    rows.push(['Annonces actives', data.funnel.listings_active]);
    rows.push(['Négociations',     data.funnel.negotiations]);
    rows.push(['Deals payés',      data.funnel.deals_paid]);
    if (ext?.funnel_4steps) {
      rows.push([]);
      rows.push(['== Funnel 4 étapes ==']);
      rows.push(['Vues distinctes', ext.funnel_4steps.views]);
      rows.push(['Négos',           ext.funnel_4steps.negotiations]);
      rows.push(['Validées',        ext.funnel_4steps.validated]);
      rows.push(['Payées',          ext.funnel_4steps.paid]);
    }
    rows.push([]);
    rows.push(['== Top prestataires ==']);
    rows.push(['Nom', 'Téléphone', 'Deals', 'CA total', 'Note moy.']);
    data.top_providers.forEach(p => rows.push([p.name, p.phone, p.deals_count, p.total_revenue, Number(p.avg_rating).toFixed(1)]));
    if (ext?.revenue_by_commune?.length) {
      rows.push([]);
      rows.push(['== CA par commune ==']);
      rows.push(['Commune', 'Deals', 'Revenue']);
      ext.revenue_by_commune.forEach(c => rows.push([c.commune, c.deals, c.revenue]));
    }
    const csv = rows.map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}><div className="spinner" /></div>;
  if (!data) return <div className="card" style={{ padding: 24, textAlign: 'center' }}>Erreur de chargement</div>;

  return (
    <div className="animate-fade">
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Analytics</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Funnel de conversion, top prestataires, cohortes, géo</p>
        </div>
        <button onClick={exportCsv} style={{ background: 'var(--primary)', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          ⬇ Export CSV
        </button>
      </div>

      {/* Funnel 4 étapes — cards cliquables */}
      {ext?.funnel_4steps && (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Funnel 4 étapes (vues → paiement) · Cliquez pour le détail</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 28 }}>
            <KpiCard label="Vues distinctes" value={fmt(ext.funnel_4steps.views)} icon="👁" color="#8b5cf6"
              onClick={() => setModal({ type: 'views', title: '👁 Vues distinctes' })} />
            <KpiCard label="Négociations" value={fmt(ext.funnel_4steps.negotiations)} sublabel={`${ext.funnel_4steps.conv_view_to_neg}% conv.`} icon="💬" color="#3b82f6"
              onClick={() => setModal({ type: 'negotiations', title: '💬 Négociations' })} />
            <KpiCard label="Validées" value={fmt(ext.funnel_4steps.validated)} sublabel={`${ext.funnel_4steps.conv_neg_to_validated}% conv.`} icon="✓" color="#f59e0b"
              onClick={() => setModal({ type: 'validated', title: '✓ Validées' })} />
            <KpiCard label="Payées" value={fmt(ext.funnel_4steps.paid)} sublabel={`${ext.funnel_4steps.conv_validated_to_paid}% conv.`} icon="💰" color="#10b981"
              onClick={() => setModal({ type: 'paid', title: '💰 Deals payés' })} />
          </div>
        </>
      )}

      {/* Modal de détails par KPI */}
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.title || ''} maxWidth={640}>
        {modal && <KpiDetail type={modal.type} data={data} ext={ext} />}
      </Modal>

      {/* Funnel — cards cliquables */}
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Funnel de conversion · Cliquez pour le détail</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
        <KpiCard label="Annonces actives" value={fmt(data.funnel.listings_active)} icon="📋" color="#3b82f6"
          onClick={() => setModal({ type: 'listings_active', title: '📋 Annonces actives' })} />
        <KpiCard label="Négociations" value={fmt(data.funnel.negotiations)} sublabel={`${data.funnel.conv_listing_to_neg}% des annonces`} icon="💬" color="#f59e0b"
          onClick={() => setModal({ type: 'negotiations', title: '💬 Négociations' })} />
        <KpiCard label="Deals payés" value={fmt(data.funnel.deals_paid)} sublabel={`${data.funnel.conv_neg_to_paid}% des négos`} icon="💰" color="#10b981"
          onClick={() => setModal({ type: 'paid', title: '💰 Deals payés' })} />
      </div>

      {/* Top prestataires */}
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Top 10 prestataires (CA conclu)</h3>
      <div className="card" style={{ marginBottom: 28 }}>
        <div className="card-body">
          {data.top_providers.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Aucun deal conclu pour l'instant</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                  <th style={th}>Rang</th>
                  <th style={th}>Prestataire</th>
                  <th style={{ ...th, textAlign: 'right' }}>Deals</th>
                  <th style={{ ...th, textAlign: 'right' }}>CA total</th>
                  <th style={{ ...th, textAlign: 'right' }}>Note moy.</th>
                </tr>
              </thead>
              <tbody>
                {data.top_providers.map((p, i) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ ...td, fontWeight: 800, fontSize: 16 }}>#{i + 1}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={p.name} src={p.avatar_url} size="sm" />
                        <div>
                          <div style={{ fontWeight: 600 }}>{p.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{p.deals_count}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--success)' }}>{fmt(p.total_revenue)} F</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {Number(p.avg_rating) > 0 ? `⭐ ${Number(p.avg_rating).toFixed(1)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Cohortes */}
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Inscriptions par mois (6 derniers mois)</h3>
      <div className="card" style={{ marginBottom: 28 }}>
        <div className="card-body" style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.cohorts}>
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="signups" radius={[8, 8, 0, 0]}>
                {data.cohorts.map((_, i) => <Cell key={i} fill={COLORS_BAR[i % COLORS_BAR.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* CA par commune */}
      {ext?.revenue_by_commune?.length > 0 && (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>CA par commune (deals payés)</h3>
          <div className="card" style={{ marginBottom: 28 }}>
            <div className="card-body">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                    <th style={{ padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)' }}>Commune</th>
                    <th style={{ padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)', textAlign: 'right' }}>Deals</th>
                    <th style={{ padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)', textAlign: 'right' }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {ext.revenue_by_commune.map(c => (
                    <tr key={c.commune} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 8px', fontWeight: 600 }}>{c.commune}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700 }}>{c.deals}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 800, color: 'var(--success)' }}>{fmt(c.revenue)} F</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Rétention cohortes */}
      {ext?.retention_cohorts?.length > 0 && (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Rétention par cohorte</h3>
          <div className="card" style={{ marginBottom: 28 }}>
            <div className="card-body">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                    <th style={{ padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)' }}>Mois</th>
                    <th style={{ padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)', textAlign: 'right' }}>Inscrits</th>
                    <th style={{ padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)', textAlign: 'right' }}>Actifs J7</th>
                    <th style={{ padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)', textAlign: 'right' }}>Actifs J30</th>
                  </tr>
                </thead>
                <tbody>
                  {ext.retention_cohorts.map(c => {
                    const d7  = c.signups > 0 ? Math.round((c.active_d7  / c.signups) * 100) : 0;
                    const d30 = c.signups > 0 ? Math.round((c.active_d30 / c.signups) * 100) : 0;
                    return (
                      <tr key={c.month} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '10px 8px', fontWeight: 600 }}>{c.month}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700 }}>{c.signups}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right' }}>{c.active_d7} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({d7}%)</span></td>
                        <td style={{ padding: '10px 8px', textAlign: 'right' }}>{c.active_d30} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({d30}%)</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Distribution des notes */}
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Distribution des notes</h3>
      <div className="card">
        <div className="card-body">
          {data.ratings_distribution.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Pas encore d'avis</div>
          ) : (
            data.ratings_distribution.map(r => {
              const total = data.ratings_distribution.reduce((s, x) => s + Number(x.n), 0);
              const pct = total > 0 ? (r.n / total * 100) : 0;
              return (
                <div key={r.rating} style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 10 }}>
                  <div style={{ width: 70, fontSize: 13, fontWeight: 700 }}>{'⭐'.repeat(r.rating)}</div>
                  <div style={{ flex: 1, height: 14, background: 'var(--border)', borderRadius: 7, overflow: 'hidden' }}>
                    <div style={{ height: 14, width: `${pct}%`, background: '#FBBF24', transition: 'width 0.6s' }} />
                  </div>
                  <div style={{ width: 80, textAlign: 'right', fontSize: 12, fontWeight: 600 }}>{r.n} ({pct.toFixed(0)}%)</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function KpiDetail({ type, data, ext }) {
  const f = ext?.funnel_4steps || {};
  const totalNeg = f.negotiations || 0;
  if (type === 'listings_active') {
    const cats = data?.top_categories || [];
    return (
      <div>
        <p style={{ fontSize: 14, marginBottom: 12 }}>
          <strong>{fmt(data?.funnel?.listings_active || 0)}</strong> annonces actives sur la plateforme (visible côté mobile).
        </p>
        <Row label="Convertes en négociations" value={`${data?.funnel?.conv_listing_to_neg || 0}%`} />
        {cats.length > 0 && (
          <>
            <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 16, marginBottom: 8 }}>Top catégories (demandes)</p>
            {cats.slice(0, 6).map((c) => (
              <div key={c.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 13 }}>{c.name}</span>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{c.demand} négos</span>
              </div>
            ))}
          </>
        )}
      </div>
    );
  }
  if (type === 'views') {
    return (
      <div>
        <p style={{ fontSize: 14, marginBottom: 12 }}>
          <strong>{fmt(f.views)}</strong> vues distinctes d'annonces enregistrées (1 vue par paire utilisateur × service).
        </p>
        <Row label="Vues → Négociations" value={`${f.conv_view_to_neg}%`} />
        <Row label="Vues qui ne deviennent pas négos" value={`${(100 - f.conv_view_to_neg).toFixed(1)}%`} />
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
          Une vue est tracée à chaque ouverture d'une fiche annonce. Tu peux augmenter ce taux en améliorant les fiches (photos, descriptions, prix).
        </p>
      </div>
    );
  }
  if (type === 'negotiations') {
    return (
      <div>
        <p style={{ fontSize: 14, marginBottom: 12 }}>
          <strong>{fmt(f.negotiations)}</strong> négociations lancées au total.
        </p>
        <Row label="Négos → Validées" value={`${f.conv_neg_to_validated}%`} />
        <Row label="Validées → Payées" value={`${f.conv_validated_to_paid}%`} />
        <Row label="Taux de conclusion global (négos → payées)" value={`${totalNeg > 0 ? ((f.paid / totalNeg) * 100).toFixed(1) : 0}%`} />
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
          Une négociation est lancée dès qu'un client propose un prix sur une annonce.
        </p>
      </div>
    );
  }
  if (type === 'validated') {
    return (
      <div>
        <p style={{ fontSize: 14, marginBottom: 12 }}>
          <strong>{fmt(f.validated)}</strong> négociations dont le prix a été accepté par les deux parties.
        </p>
        <Row label="Validées → Payées" value={`${f.conv_validated_to_paid}%`} />
        <Row label="Validées en attente de paiement" value={fmt(f.validated - f.paid)} />
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
          Le taux validées → payées révèle un trou potentiel : si bas, des accords ne se concrétisent pas (problème de paiement Mobile Money ?).
        </p>
      </div>
    );
  }
  if (type === 'paid') {
    const top = (data?.top_providers || []).slice(0, 5);
    return (
      <div>
        <p style={{ fontSize: 14, marginBottom: 12 }}>
          <strong>{fmt(f.paid)}</strong> deals conclus (commission 10% payée).
        </p>
        <Row label="Revenue commission (estimation)" value={`${fmt(top.reduce((s, p) => s + Number(p.total_revenue || 0), 0) * 0.10)} F`} />
        {top.length > 0 && (
          <>
            <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 16, marginBottom: 8 }}>Top 5 prestataires</p>
            {top.map((p, i) => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 13 }}>{i + 1}. {p.name}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--success)' }}>{fmt(p.total_revenue)} F ({p.deals_count} deals)</span>
              </div>
            ))}
          </>
        )}
      </div>
    );
  }
  return null;
}

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{label}</span>
    <span style={{ fontSize: 14, fontWeight: 700 }}>{value}</span>
  </div>
);

const th = { padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)' };
const td = { padding: '12px 8px', color: 'var(--text-primary)' };
